import { Plus, Trash } from '@phosphor-icons/react';
import { describeRepeat, type Reward, type Task } from '@shh/shared';
import { Avatar, Button, Icon, IconButton, PointsPill, Sheet, Text, TextField } from '@shh/ui';
import { useState, type ReactNode } from 'react';
import { Button as AriaButton } from 'react-aria-components';
import { RewardForm } from '../../components/forms';
import { JobForm } from '../../components/taskForms';
import { deleteReward, deleteTask, saveGoal } from '../../data/api';
import { memberById } from '../../data/derive';
import { useFamily } from '../../data/family';
import { iconFor } from '../../lib/icons';
import { locale } from '../../lib/locale';
import { formsText } from '../../i18n/forms';
import { settingsText } from '../../i18n/settings';

const DAYPART = settingsText.dayparts;

/** Settings → Aufgaben: everything that repeats, who it belongs to, how many stars. */
export function TaskSettings() {
  const f = useFamily();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Task>();
  const tasks = f.tasks
    .filter((t) => t.source !== 'waste')
    .sort((a, b) => a.title.localeCompare(b.title));
  // kids first, then the parents – their own tasks (since 1.9) were missing here (Mike, 04.10.)
  const people = [
    ...f.members.filter((m) => m.role !== 'parent'),
    ...f.members.filter((m) => m.role === 'parent'),
  ];
  const groups = [
    ...people.map((m) => ({
      key: m.id,
      label: m.name,
      member: m,
      items: tasks.filter(
        (t) => Array.isArray(t.assignees) && t.assignees.length === 1 && t.assignees[0] === m.id,
      ),
    })),
    {
      key: 'shared',
      label: settingsText.sharedFirst,
      member: undefined,
      items: tasks.filter((t) => Array.isArray(t.assignees) && t.assignees.length > 1),
    },
    {
      key: 'all',
      label: settingsText.forAll,
      member: undefined,
      items: tasks.filter((t) => t.assignees === 'all'),
    },
  ].filter((g) => g.items.length);

  return (
    <div className="flex flex-col gap-gap-lg">
      {groups.map((g) => (
        <section key={g.key} className="flex flex-col gap-gap-xs" aria-label={g.label}>
          <h3 className="flex items-center gap-gap-sm font-display font-black">
            {g.member && (
              <Avatar
                name={g.member.name}
                color={g.member.color}
                emoji={g.member.emoji}
                size="sm"
                decorative
              />
            )}
            {g.label}
          </h3>
          <ul className="flex flex-col gap-gap-xs">
            {g.items.map((t) => (
              <li
                key={t.id}
                className="flex items-center gap-gap-sm rounded-control bg-surface-raised p-inset-xs"
              >
                <EditButton label={settingsText.edit(t.title)} onPress={() => setEditing(t)}>
                  <Icon icon={iconFor(t.icon)} size="md" className="text-text-secondary" />
                  <span className="flex flex-1 flex-col">
                    <span className="font-semibold">{t.title}</span>
                    <span className="text-xs text-text-secondary">
                      {describeRepeat(t.repeat, locale)} · {DAYPART[t.daypart]}
                      {t.requiresApproval ? settingsText.withApproval : ''}
                      {Array.isArray(t.assignees) && t.assignees.length > 1
                        ? ` · ${t.assignees.map((id) => memberById(f, id)?.name).join(', ')}`
                        : ''}
                    </span>
                  </span>
                </EditButton>
                {t.points > 0 && <PointsPill points={t.points} signed unit={settingsText.stars} />}
                <IconButton
                  icon={Trash}
                  aria-label={settingsText.deleteItem(t.title)}
                  onPress={() => void deleteTask(f.householdId, t.id)}
                />
              </li>
            ))}
          </ul>
        </section>
      ))}
      {!groups.length && <Text tone="secondary">{settingsText.noTasks}</Text>}
      <Button icon={Plus} onPress={() => setAdding(true)} className="self-start">
        {settingsText.addTask}
      </Button>
      <Sheet
        isOpen={adding}
        onOpenChange={setAdding}
        title={settingsText.newTask}
        isDismissable={false}
      >
        <JobForm preset={{ often: 'regular' }} onDone={() => setAdding(false)} />
      </Sheet>
      <Sheet
        isOpen={!!editing}
        onOpenChange={(open) => !open && setEditing(undefined)}
        title={settingsText.editTask}
        isDismissable={false}
      >
        {editing && <JobForm task={editing} onDone={() => setEditing(undefined)} />}
      </Sheet>
    </div>
  );
}

/** Settings → Belohnungen & Familienziel */
export function RewardSettings() {
  const f = useFamily();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Reward>();
  const [title, setTitle] = useState<string>(f.goal?.title ?? settingsText.goalDefault);
  const [target, setTarget] = useState(String(f.goal?.target ?? 2000));
  const [saved, setSaved] = useState(false);
  return (
    <div className="flex flex-col gap-gap-lg">
      <ul className="flex flex-col gap-gap-xs">
        {[...f.rewards]
          .sort((a, b) => a.cost - b.cost)
          .map((r) => (
            <li
              key={r.id}
              className="flex items-center gap-gap-sm rounded-control bg-surface-raised p-inset-xs"
            >
              <EditButton label={settingsText.edit(r.title)} onPress={() => setEditing(r)}>
                <Icon icon={iconFor(r.icon)} size="md" className="text-accent-tertiary" />
                <span className="flex flex-1 flex-col">
                  <span className="font-semibold">{r.title}</span>
                  <span className="text-xs text-text-secondary">
                    {r.memberIds.length
                      ? r.memberIds.map((id) => memberById(f, id)?.name).join(', ')
                      : settingsText.forAllKids}
                  </span>
                </span>
              </EditButton>
              <PointsPill points={r.cost} unit={settingsText.stars} />
              <IconButton
                icon={Trash}
                aria-label={settingsText.deleteItem(r.title)}
                onPress={() => void deleteReward(f.householdId, r.id)}
              />
            </li>
          ))}
      </ul>
      <Button
        variant="secondary"
        icon={Plus}
        onPress={() => setAdding(true)}
        className="self-start"
      >
        {settingsText.addReward}
      </Button>

      <form
        className="flex flex-col gap-gap-md rounded-card bg-surface-sunken p-inset-sm"
        onSubmit={(e) => {
          e.preventDefault();
          void saveGoal(f.householdId, title.trim(), Number(target)).then(() => setSaved(true));
        }}
      >
        <Text weight="semibold">{settingsText.goalTitle}</Text>
        <div className="grid grid-cols-1 gap-gap-md sm:grid-cols-[1fr_10rem]">
          <TextField
            label={settingsText.goal}
            value={title}
            onChange={(v) => {
              setTitle(v);
              setSaved(false);
            }}
          />
          <TextField
            label={formsText.stars}
            inputMode="numeric"
            value={target}
            onChange={(v) => {
              setTarget(v.replace(/\D/g, ''));
              setSaved(false);
            }}
          />
        </div>
        <div className="flex items-center gap-gap-md">
          <Button type="submit" variant="secondary" isDisabled={!title.trim() || !Number(target)}>
            {settingsText.saveGoal}
          </Button>
          {saved && (
            <span role="status" className="text-sm font-semibold text-feedback-success">
              {settingsText.saved}
            </span>
          )}
        </div>
      </form>

      <Sheet
        isOpen={adding}
        onOpenChange={setAdding}
        title={settingsText.newReward}
        isDismissable={false}
      >
        <RewardForm onDone={() => setAdding(false)} />
      </Sheet>
      <Sheet
        isOpen={!!editing}
        onOpenChange={(open) => !open && setEditing(undefined)}
        title={settingsText.editReward}
        isDismissable={false}
      >
        {editing && <RewardForm reward={editing} onDone={() => setEditing(undefined)} />}
      </Sheet>
    </div>
  );
}

/** The tappable part of a list row: opens the editor (the bin next to it stays separate). */
function EditButton({
  label,
  onPress,
  children,
}: {
  label: string;
  onPress: () => void;
  children: ReactNode;
}) {
  return (
    <AriaButton
      aria-label={label}
      onPress={onPress}
      className="flex min-h-touch flex-1 cursor-pointer items-center gap-gap-sm rounded-control text-left outline-none data-focus-visible:outline-3 data-focus-visible:outline-border-focus"
    >
      {children}
    </AriaButton>
  );
}
